import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';

// Navigation
import { Link } from 'react-router-dom';

const CartDrawer = ({ isOpen, onClose, cart, onRemove, onUpdateQty }) => {
    const navigate = useNavigate();
    const total = cart.reduce((sum, item) => sum + item.price * item.qty, 0);

    const handleCheckout = () => {
        onClose();
        navigate('/Boutique/checkout');
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-[100] "
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                    />

                    {/* Drawer */}
                    <motion.aside
                        className="fixed right-0 top-0 h-full w-full max-w-md bg-white dark:bg-store-dark border-l border-slate-200 dark:border-store-border z-[110] flex flex-col shadow-2xl transition-colors duration-300"
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 dark:border-store-border">
                            <div className="flex items-center gap-2">
                                <Icon icon="solar:cart-large-bold-duotone" className="text-2xl text-lime-600 dark:text-lime-400" />
                                <h2 className="text-slate-900 dark:text-white font-bold text-lg">Mon Panier</h2>
                                {cart.length > 0 && (
                                    <span className="bg-lime-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                                        {cart.reduce((s, i) => s + i.qty, 0)}
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={onClose}
                                className="text-slate-400 dark:text-store-gray hover:text-slate-900 dark:hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-store-gray-dark"
                            >
                                <Icon icon="solar:close-circle-bold" className="text-xl" />
                            </button>
                        </div>

                        {/* Items */}
                        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                            {cart.length === 0 ? (
                                <motion.div
                                    className="flex flex-col items-center justify-center h-full text-center gap-4"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <div className="w-20 h-20 rounded-full bg-slate-50 dark:bg-store-gray-dark/50 border border-slate-100 dark:border-store-border flex items-center justify-center">
                                        <Icon icon="solar:cart-large-minimalistic-bold-duotone" className="text-4xl text-slate-300 dark:text-store-gray" />
                                    </div>
                                    <p className="text-slate-500 dark:text-store-gray text-sm font-medium">Votre panier est vide</p>
                                    <Link
                                        to="/Boutique"
                                        className="flex items-center justify-center gap-2 text-lime-600 dark:text-lime-400 text-sm font-semibold hover:underline"
                                    >
                                        Continuer mes achats
                                        <Icon icon="guidance:left-arrow" className="text-xl" />
                                    </Link>
                                </motion.div>
                            ) : (
                                <AnimatePresence>
                                    {cart.map((item) => (
                                        <motion.div
                                            key={item.cartId}
                                            layout
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20, height: 0 }}
                                            transition={{ duration: 0.25 }}
                                            className="flex gap-3 p-3 bg-slate-50/50 dark:bg-store-black/50 border border-slate-100 dark:border-store-border rounded-xl"
                                        >
                                            {/* Thumbnail */}
                                            <img
                                                src={item.images[0]}
                                                alt={item.name}
                                                className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                            />

                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-slate-900 dark:text-white text-xs font-semibold line-clamp-1 leading-tight mb-0.5">
                                                    {item.name}
                                                </p>
                                                
                                                {/* Options summary */}
                                                {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mb-1">
                                                        {Object.entries(item.selectedOptions).map(([optId, valId]) => {
                                                            const opt = item.options?.find(o => o.id === optId);
                                                            const val = opt?.values.find(v => v.id === valId);
                                                            return (
                                                                <span key={optId} className="text-[9px] px-1.5 py-0.5 bg-slate-200 dark:bg-store-gray-dark text-slate-500 dark:text-store-gray rounded-md border border-slate-300 dark:border-store-border">
                                                                    {val?.label}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                <p className="text-lime-600 dark:text-lime-400 font-bold text-sm">
                                                    {(item.price * item.qty).toFixed(2)} $US
                                                </p>

                                                {/* Qty controls */}
                                                <div className="flex items-center gap-2 mt-2">
                                                    <button
                                                        onClick={() => onUpdateQty(item.cartId, item.qty - 1)}
                                                        className="w-6 h-6 rounded-md bg-white dark:bg-store-gray-dark/70 hover:bg-slate-100 dark:hover:bg-store-gray-dark text-slate-600 dark:text-white border border-slate-200 dark:border-transparent flex items-center justify-center text-xs transition-colors"
                                                    >
                                                        <Icon icon="solar:minus-circle-bold" />
                                                    </button>
                                                    <span className="text-slate-900 dark:text-white text-xs w-4 text-center font-mono font-bold">{item.qty}</span>
                                                    <button
                                                        onClick={() => onUpdateQty(item.cartId, item.qty + 1)}
                                                        className="w-6 h-6 rounded-md bg-white dark:bg-store-gray-dark/70 hover:bg-slate-100 dark:hover:bg-store-gray-dark text-slate-600 dark:text-white border border-slate-200 dark:border-transparent flex items-center justify-center text-xs transition-colors"
                                                    >
                                                        <Icon icon="solar:add-circle-bold" />
                                                    </button>

                                                    <button
                                                        onClick={() => onRemove(item.cartId)}
                                                        className="ml-auto text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                                    >
                                                        <Icon icon="solar:trash-bin-minimalistic-bold" className="text-base" />
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            )}
                        </div>

                        {/* Footer */}
                        {cart.length > 0 && (
                            <div className="px-6 py-5 border-t border-slate-200 dark:border-store-border space-y-4">
                                {/* Summary */}
                                <div className="space-y-2">
                                    <div className="flex justify-between text-slate-500 dark:text-store-gray text-xs font-medium">
                                        <span>Sous-total</span>
                                        <span>{total.toFixed(2)} $US</span>
                                    </div>
                                    <div className="flex justify-between text-slate-500 dark:text-store-gray text-xs font-medium">
                                        <span>Livraison</span>
                                        <span className="text-lime-600 dark:text-lime-400"> Gratuite à partir de 499 $US</span>
                                    </div>
                                    <div className="flex justify-between text-slate-900 dark:text-white font-bold text-base pt-2 border-t border-slate-200 dark:border-store-border">
                                        <span>Total</span>
                                        <span className="text-lime-600 dark:text-lime-400">{total.toFixed(2)} $US</span>
                                    </div>
                                </div>

                                {/* Checkout button */}
                                <motion.button
                                    onClick={handleCheckout}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full bg-lime-500 hover:bg-lime-400 text-black font-bold py-3.5 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(163,230,53,0.3)] shadow-lime-500/20"
                                >
                                    <Icon icon="solar:card-bold-duotone" className="text-xl" />
                                    Commander — {total.toFixed(2)} $US
                                </motion.button>

                                <button
                                    onClick={onClose}
                                    className="flex items-center justify-center gap-2 w-full text-slate-400 dark:text-store-gray hover:text-slate-900 dark:hover:text-white text-xs font-medium py-2 transition-colors text-center"
                                >
                                    <Icon icon="guidance:right-arrow" className="text-xl" /> Continuer mes achats
                                </button>
                            </div>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
};

export default CartDrawer;
